import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Lightbulb, GraduationCap, Mail, Briefcase, FileText, Users } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { PageHeader } from '../components/SectionHeader';
import { AdminUsers } from '../components/AdminUsers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { id: 'suggestions', label: 'Sugerencias', icon: Lightbulb }, { id: 'training_proposals', label: 'Cursos propuestos', icon: GraduationCap },
  { id: 'contact_messages', label: 'Mensajes', icon: Mail }, { id: 'job_drafts', label: 'Ofertas en revisión', icon: Briefcase },
  { id: 'pending_documents', label: 'Documentos', icon: FileText }, { id: 'users', label: 'Usuarios', icon: Users, admin: true },
];
const STATUS_OPTS = ['new', 'reviewing', 'done', 'rejected', 'pending_review', 'approved'];

const Inner = () => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('suggestions');
  const [err, setErr] = useState(null);
  const load = () => fetch(`${API}/admin/overview`, { credentials: 'include' }).then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail); return r.json(); }).then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);
  const setStatus = async (collection, id, status) => {
    const res = await fetch(`${API}/admin/items/${collection}/${id}/status`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ status }) });
    if (res.ok) { toast.success('Estado actualizado'); load(); } else toast.error('Error al actualizar');
  };
  if (err) return <Shell><div className="sc-card p-8 text-center text-slate-300" data-testid="admin-forbidden">{err}. Esta zona es solo para administradores y moderadores.</div></Shell>;
  if (!data) return <Shell><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto my-16" /></Shell>;
  const isAdmin = data.role === 'admin';
  const rows = data[tab] || [];
  const sel = 'bg-[#0B0C0E] border border-[#2A2F3D] rounded-md px-2 py-1 text-xs text-slate-200';
  return (
    <Shell>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-tabs">
        {TABS.filter((t) => !t.admin || isAdmin).map((t) => { const Icon = t.icon; const n = t.id === 'users' ? data.user_count : (data[t.id] || []).length; return (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest rounded-full border transition-colors ${tab === t.id ? 'bg-amber-500 text-slate-950 border-amber-500' : 'text-slate-300 border-[#2A2F3D] hover:border-amber-500/40'}`}><Icon size={12} /> {t.label} <span className="opacity-70">({n})</span></button>
        ); })}
      </div>
      {tab === 'users' ? <AdminUsers /> : <div className="sc-card divide-y divide-[#2A2F3D] mb-16" data-testid={`admin-list-${tab}`}>
        {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500">Nada pendiente aquí.</div>}
        {tab === 'pending_documents' ? rows.map((d) => (
          <div key={d.id} className="p-4 flex items-center gap-3"><div className="flex-1 min-w-0"><div className="text-sm text-white truncate">{d.title}</div><div className="text-xs text-slate-500">{d.category} · {d.uploader?.name}</div></div><Link to="/biblioteca" className="text-[11px] font-mono uppercase tracking-widest text-amber-500 hover:underline">Revisar en Biblioteca →</Link></div>
        )) : rows.map((r) => (
          <div key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`admin-item-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white">{r.title || r.message?.slice(0, 140) || r.topic}</div>
              <div className="text-xs text-slate-500 font-mono mt-1 truncate">{r.user_name || r.name || r.owner_email || r.school} · {r.user_email || r.email || r.owner_email || ''} · {new Date(r.created_at).toLocaleString('es-ES')}{r.page ? ` · ${r.page}` : ''}{r.category ? ` · ${r.category}` : ''}</div>
              {r.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{r.description}</p>}
              {r.title && r.message && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{r.message}</p>}
            </div>
            <select data-testid={`admin-status-${r.id}`} className={sel} value={r.status || 'new'} onChange={(e) => setStatus(tab, r.id, e.target.value)}>{STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}</select>
          </div>
        ))}
      </div>}
    </Shell>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0B0C0E]"><Navbar />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <PageHeader title="Administración y moderación" subtitle="Revisa sugerencias, cursos, mensajes, ofertas y documentos. Los administradores además gestionan roles y vetos de usuarios e IP." testId="admin-header" />
      <div className="flex items-center gap-2 text-sky-400 text-xs font-mono uppercase tracking-widest mb-6"><ShieldCheck size={13} /> Zona restringida</div>
      {children}
    </div><Footer /></div>
);

const AdminPage = () => <AuthGate eyebrow="Administración"><Inner /></AuthGate>;
export default AdminPage;
